import {useAppContext} from 'contexts/ApplicationContext';
import {useProductContext} from 'contexts/ProductContext';
import {useFormikContext} from 'formik';
import React, {useCallback, useEffect, useRef} from 'react';
import {InputGroup} from 'react-bootstrap';
import {FormControl} from 'shared/components/Forms/FormControl';
import {Icon} from 'shared/helpers';
import {DoorHinge} from 'shared/types';
import excel from 'shared/Excel';
import {useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {Cabinet} from 'components/customer/Product/entity/Cabinet';
import {omit} from 'lodash';

type SizeType = {
    name: string;
    value: string | number;
    metric: string;
    hideDialog?: () => void;
    inSpecsTab?: boolean;
    isQFP?: boolean;
    withDeleteIcon?: boolean;
    deleteAction?: () => void;
    customWidth?: string;
    selectHandler?: (name: string, value: number) => void;
    disabled?: boolean;
    isInvalid?: boolean;
    fieldSetIndex?: number;
    liveUpdate?: boolean;
    error?: string;
    productDataStore?: ProductDataStore & {
        current?: {
            cabinet?: {
                attributes?: Cabinet;
            };
        };
    };
};

export const Size = ({
    name,
    value,
    metric,
    hideDialog,
    isQFP,
    inSpecsTab,
    withDeleteIcon,
    deleteAction,
    customWidth,
    ...otherParams
}: SizeType): JSX.Element => {
    const passToChildren = omit(otherParams, ['productDataStore']);

    const {getMaterialOptions, isMiniBrowser} = useProductContext<{
        getMaterialOptions: () => object;
        isMiniBrowser: boolean;
    }>();
    const {userProfile} = useAppContext();
    const formik = useFormikContext<{[key: string]: number}>();
    const [searchParams] = useSearchParams();

    const updateFields = useRef<string[]>([
        'rem_door_shelf_height',
        'cabinet_height',
    ]).current;

    const cabinetId = searchParams.get('cabinetId');

    const updateHingeHolesAmount = useCallback(
        (height: number) => {
            if (formik) {
                let hingeReference: DoorHinge[] = [
                    {
                        amount: 3,
                        height: 900,
                    },
                    {
                        amount: 4,
                        height: 1500,
                    },
                    {
                        amount: 5,
                        height: 2200,
                    },
                ];
                let newHingeAmount = 2;

                if (
                    userProfile.doorHingeHoles &&
                    typeof userProfile.doorHingeHoles == 'object' &&
                    Object.keys(userProfile.doorHingeHoles).length > 0
                ) {
                    const amounts = Object.keys(userProfile.doorHingeHoles);

                    hingeReference = amounts.map(
                        (amount) =>
                            ({
                                amount: Number(amount),
                                height: userProfile.doorHingeHoles[
                                    Number(amount)
                                ],
                            }) as DoorHinge
                    );
                }

                hingeReference.forEach((hingeRef) => {
                    if (height >= hingeRef.height) {
                        newHingeAmount = hingeRef.amount;
                    }
                });

                void formik.setFieldValue('door_hinge_amount', newHingeAmount);
                void formik.setFieldValue(
                    'door_hinge_amount_1',
                    newHingeAmount
                );
            }
        },
        [userProfile]
    );

    useEffect(() => {
        if (
            (typeof cabinetId === 'undefined' || cabinetId == null) &&
            updateFields.includes(name) &&
            formik &&
            formik.values.hasOwnProperty('door_hinge_amount')
        ) {
            if (
                name === 'cabinet_height' &&
                formik.values.hasOwnProperty('rem_door_shelf_height')
            ) {
                return;
            }

            const height = excel.calculate(
                String(formik.values[String(name)]),
                {
                    ...formik.values,
                    ...getMaterialOptions(),
                }
            );

            updateHingeHolesAmount(Number(height));
        }
        return () => {
            hideDialog && hideDialog();
        };
    }, [value, cabinetId]);

    useEffect(() => {
        // this effect is particular for shelves only
        // when value is forced to zero -- we need to sync it to the formik context
        const {fieldSetIndex, productDataStore} = otherParams;
        const values = formik.values as unknown as {
            shelves: [{shelf_offset: number}];
            upper_shelves: [{upper_shelf_offset: number}];
            lower_shelves: [{lower_shelf_offset: number}];
        };
        if (
            name === 'shelf_offset' &&
            value != values['shelves'][Number(fieldSetIndex)][`${name}`] &&
            productDataStore?.current?.cabinet?.attributes?.style == 2 && // Tall
            productDataStore?.current?.cabinet?.attributes?.sub_style_id == 6 // Tall Return Blind
        ) {
            void formik.setFieldValue(
                `shelves[${fieldSetIndex}].shelf_offset`,
                value
            );
        }

        if (
            name === 'upper_shelf_offset' &&
            value !=
                values['upper_shelves'][Number(fieldSetIndex)][`${name}`] &&
            productDataStore?.current?.cabinet?.attributes?.style == 4 && // Pantry
            productDataStore?.current?.cabinet?.attributes?.sub_style_id == 38 // Pantry Return Blind
        ) {
            void formik.setFieldValue(
                `upper_shelves[${fieldSetIndex}].upper_shelf_offset`,
                value
            );
        }

        if (
            name === 'lower_shelf_offset' &&
            value !=
                values['lower_shelves'][Number(fieldSetIndex)][`${name}`] &&
            productDataStore?.current?.cabinet?.attributes?.style == 4 && // Pantry
            productDataStore?.current?.cabinet?.attributes?.sub_style_id == 38 // Pantry Return Blind
        ) {
            void formik.setFieldValue(
                `lower_shelves[${fieldSetIndex}].lower_shelf_offset`,
                value
            );
        }
    }, [value]);

    useEffect(() => {
        // doing this for every fields yields unwanted side effets
        // so we only update formik when the field is drawer_hori_height
        if (name != 'drawer_hori_height') return;
        if (!formik?.values) return;

        const currentValue = formik.values[String(name)];
        if (currentValue !== value) {
            void formik.setFieldValue(name, value);
        }
    }, [value]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const invalidKeys = new Set(['.', '-', '+', 'e', 'E']);
            const offsetFields = [
                'shelf_offset',
                'upper_shelf_offset',
                'lower_shelf_offset',
            ];

            const isOffsetField = offsetFields.includes(name);
            const isInvalidKey = invalidKeys.has(e.key);

            if (isOffsetField && isInvalidKey) {
                e.preventDefault();
            }
        },
        [value]
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLInputElement>) => {
            const {fieldSetIndex} = otherParams;

            const fieldArray =
                name === 'shelf_offset'
                    ? 'shelves'
                    : name === 'upper_shelf_offset'
                      ? 'upper_shelves'
                      : name === 'lower_shelf_offset'
                        ? 'lower_shelves'
                        : undefined;

            if (fieldArray) {
                e.preventDefault();

                const pastedText = e.clipboardData.getData('Text').trim();
                const numericValue = Number(pastedText);
                const integerPart = Math.trunc(numericValue);

                if (!isNaN(integerPart)) {
                    void formik.setFieldValue(
                        `${fieldArray}[${fieldSetIndex}].${name}`,
                        Math.max(0, integerPart)
                    );
                }
            }
        },
        [value]
    );

    return (
        <InputGroup
            className="cbc-size"
            style={{
                width: !!customWidth
                    ? customWidth
                    : inSpecsTab && !isQFP
                      ? '150px'
                      : 'auto',
            }}>
            <FormControl
                style={
                    isMiniBrowser
                        ? {
                              fontSize: '0.95em',
                              padding: '5px',
                          }
                        : {}
                }
                {...passToChildren}
                type="number"
                name={name}
                value={value}
                isQFP={isQFP}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
            />
            <InputGroupText $isQFP={isQFP}>{metric}</InputGroupText>
            {withDeleteIcon ? (
                <Icon
                    iconName="Options-Delete.svg"
                    style={{width: 22, cursor: 'pointer', margin: '0 8px'}}
                    onClick={deleteAction}
                />
            ) : null}
        </InputGroup>
    );
};

export const InputGroupText = styled(InputGroup.Text)<{$isQFP: boolean}>`
    background: transparent;
    border: 0;
    padding: 0 8px 2px;
    font-size: ${({$isQFP}) => ($isQFP ? 1.2 : 0.85)}em;
    font-weight: 500;
    color: #6b6f70;
`;
